import { useId, type KeyboardEvent, type ReactNode } from 'react';
import type { IconData } from './icon-data.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { iconBack, iconCheck } from './generated/icons.js';
import { cx, useControllable } from './util.js';

/** Moves focus between the items of a roving group with the arrow keys, Home and End. */
function roving(event: KeyboardEvent<HTMLElement>, selector: string, vertical = false) {
  const prev = vertical ? 'ArrowUp' : 'ArrowLeft';
  const next = vertical ? 'ArrowDown' : 'ArrowRight';
  if (![prev, next, 'Home', 'End'].includes(event.key)) return;
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute('disabled'),
  );
  const index = items.findIndex((el) => el === document.activeElement);
  if (index < 0 || items.length === 0) return;
  event.preventDefault();
  const target =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? items.length - 1
        : (index + (event.key === next ? 1 : -1) + items.length) % items.length;
  items[target]?.focus();
}

export interface TabItem {
  key: string;
  label: string;
  badge?: number;
}

export interface TabsProps {
  label: string;
  tabs: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (key: string) => void;
  /** Renders the panel for the selected tab. */
  panel?: (key: string) => ReactNode;
}

/** Pill tabs. Arrow keys move and select; the panel is labelled by its tab. */
export function Tabs({ label, tabs, value, defaultValue, onChange, panel }: TabsProps) {
  const [selected, setSelected] = useControllable(
    value,
    defaultValue ?? tabs[0]?.key ?? '',
    onChange,
  );
  const base = useId();
  return (
    <div className="k-tabs">
      <div
        role="tablist"
        aria-label={label}
        className="k-tabs__list"
        onKeyDown={(event) => {
          roving(event, '[role="tab"]');
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            const el = document.activeElement as HTMLElement | null;
            const key = el?.dataset['tab'];
            if (key !== undefined) setSelected(key);
          }
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`${base}-${tab.key}`}
            data-tab={tab.key}
            aria-selected={selected === tab.key}
            aria-controls={panel === undefined ? undefined : `${base}-panel-${tab.key}`}
            tabIndex={selected === tab.key ? 0 : -1}
            className={cx('k-tab k-layer k-t-label', selected === tab.key && 'k-tab--selected')}
            onClick={() => setSelected(tab.key)}
          >
            {tab.label}
            {tab.badge === undefined || tab.badge === 0 ? null : (
              <span className="k-badge k-badge--inline" aria-label={`${String(tab.badge)} new`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {panel === undefined ? null : (
        <div
          role="tabpanel"
          id={`${base}-panel-${selected}`}
          aria-labelledby={`${base}-${selected}`}
          tabIndex={0}
        >
          {panel(selected)}
        </div>
      )}
    </div>
  );
}

export interface SegmentedOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SegmentedProps {
  label: string;
  options: SegmentedOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Fill the width, every segment the same size. */
  block?: boolean;
}

/** A segmented button: one choice of a few, as a radio group with arrow keys. */
export function Segmented({
  label,
  options,
  value,
  defaultValue,
  onChange,
  block = false,
}: SegmentedProps) {
  const [selected, setSelected] = useControllable(
    value,
    defaultValue ?? options[0]?.value ?? '',
    onChange,
  );
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('k-segmented', block && 'k-segmented--block')}
      onKeyDown={(event) => {
        roving(event, '[role="radio"]');
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          const el = document.activeElement as HTMLElement | null;
          const v = el?.dataset['value'];
          if (v !== undefined) setSelected(v);
        }
      }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          data-value={option.value}
          aria-checked={selected === option.value}
          disabled={option.disabled}
          tabIndex={selected === option.value ? 0 : -1}
          className={cx(
            'k-segment k-layer k-t-label',
            selected === option.value && 'k-segment--selected',
          )}
          onClick={() => setSelected(option.value)}
        >
          {selected === option.value ? <Icon icon={iconCheck} size={18} /> : null}
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface NavItem {
  key: string;
  label: string;
  icon: IconData;
  /** The filled icon, used when the item is selected. */
  activeIcon?: IconData;
  href?: string;
  badge?: number;
}

export interface NavProps {
  label: string;
  items: NavItem[];
  current: string;
  onSelect: (key: string) => void;
}

function NavButton({
  item,
  selected,
  onSelect,
}: {
  item: NavItem;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const content = (
    <>
      <span className="k-nav__icon">
        <Icon icon={selected && item.activeIcon !== undefined ? item.activeIcon : item.icon} />
        {item.badge === undefined || item.badge === 0 ? null : (
          <span className="k-badge k-badge--corner" aria-label={`${String(item.badge)} new`}>
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </span>
      <span className="k-nav__label k-t-label-sm">{item.label}</span>
    </>
  );
  const cls = cx('k-nav__item k-layer', selected && 'k-nav__item--selected');
  return item.href === undefined ? (
    <button
      type="button"
      className={cls}
      aria-current={selected ? 'page' : undefined}
      onClick={() => onSelect(item.key)}
    >
      {content}
    </button>
  ) : (
    <a
      href={item.href}
      className={cls}
      aria-current={selected ? 'page' : undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onSelect(item.key);
      }}
    >
      {content}
    </a>
  );
}

/** The bottom navigation bar for phones: four or five items. */
export function NavigationBar({ label, items, current, onSelect }: NavProps) {
  return (
    <nav className="k-navbar" aria-label={label}>
      {items.map((item) => (
        <NavButton key={item.key} item={item} selected={item.key === current} onSelect={onSelect} />
      ))}
    </nav>
  );
}

/** The navigation rail for tablets and desktops, with a slot for the FAB. */
export function NavigationRail({
  label,
  items,
  current,
  onSelect,
  fab,
  brand,
  expanded = false,
}: NavProps & { fab?: ReactNode; brand?: ReactNode; expanded?: boolean }) {
  return (
    <nav className={cx('k-rail', expanded && 'k-rail--expanded')} aria-label={label}>
      {brand === undefined ? null : <div className="k-rail__brand">{brand}</div>}
      {fab === undefined ? null : <div className="k-rail__fab">{fab}</div>}
      {items.map((item) => (
        <NavButton key={item.key} item={item} selected={item.key === current} onSelect={onSelect} />
      ))}
    </nav>
  );
}

export interface TopAppBarProps {
  title: string;
  subtitle?: string;
  /** Large title for the root of a tab; small for a detail screen. */
  large?: boolean;
  onBack?: () => void;
  backLabel?: string;
  actions?: ReactNode;
}

export function TopAppBar({
  title,
  subtitle,
  large = false,
  onBack,
  backLabel = 'Back',
  actions,
}: TopAppBarProps) {
  return (
    <header className={cx('k-appbar', large && 'k-appbar--large')}>
      <div className="k-appbar__row">
        {onBack === undefined ? null : (
          <IconButton icon={iconBack} label={backLabel} onClick={onBack} />
        )}
        {large ? (
          <span className="k-grow" />
        ) : (
          <h1 className="k-t-title k-appbar__title">{title}</h1>
        )}
        {actions === undefined ? null : <div className="k-appbar__actions">{actions}</div>}
      </div>
      {large ? <h1 className="k-t-headline-lg">{title}</h1> : null}
      {subtitle === undefined ? null : <p className="k-t-body k-muted">{subtitle}</p>}
    </header>
  );
}

/** A floating toolbar docked above the content's end. It is part of the layout, never over a list. */
export function FloatingToolbar({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="toolbar" aria-label={label} className="k-toolbar">
      {children}
    </div>
  );
}
